import { ArrowDown, Loader2, Pencil, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { SourceVideoTable } from './SourceVideoTable'
import { AIGeneratedTable } from './AIGeneratedTable'
import type { StoryboardFrameData } from './types'

interface StoryboardComparisonProps {
  sourceFrames: StoryboardFrameData[]
  benchmarkFrames: StoryboardFrameData[]
  isAdaptingCopy?: boolean
  onBenchmarkFrameClick?: (frame: StoryboardFrameData) => void
  onBenchmarkFramePreview?: (frame: StoryboardFrameData) => void
  onBenchmarkRegenerate?: (frame: StoryboardFrameData) => void
  onBenchmarkRemove?: (frame: StoryboardFrameData) => void
  onBenchmarkUpdate?: (frameId: string, updates: Partial<StoryboardFrameData>) => void
  onRegenerateSection?: (section: 'hook' | 'all') => void
}

export function StoryboardComparison({
  sourceFrames,
  benchmarkFrames,
  isAdaptingCopy,
  onBenchmarkFrameClick,
  onBenchmarkFramePreview,
  onBenchmarkRegenerate,
  onBenchmarkRemove,
  onBenchmarkUpdate,
  onRegenerateSection,
}: StoryboardComparisonProps) {
  const hookFrames = benchmarkFrames.filter(f => f.label === 'Hook')
  const hasHookSection = hookFrames.length > 0

  return (
    <div className="overflow-x-auto">
      <div className="flex flex-col gap-4 min-w-max">
        {/* Source Video Table */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <SourceVideoTable frames={sourceFrames} />
        </div>

        {/* Divider with arrow */}
        <div className="flex items-center justify-center gap-2 py-1">
          <div className="h-px flex-1 bg-tf-border" />
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-tf-bg-hover text-tf-text-muted">
            <ArrowDown className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium">AI Generated</span>
          </div>
          <div className="h-px flex-1 bg-tf-border" />
        </div>

        {/* Generated Storyboard Section */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-blue-600 text-white">
                <Sparkles className="h-3 w-3" />
                AI Generated
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-700">
                <Pencil className="h-2.5 w-2.5" />
                Editable
              </span>
              {isAdaptingCopy && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 animate-pulse">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Adapting copy to brand...
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasHookSection && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<RefreshCw className="h-3 w-3" />}
                  onClick={() => onRegenerateSection?.('hook')}
                >
                  Regenerate Hook
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                icon={<RefreshCw className="h-3 w-3" />}
                onClick={() => onRegenerateSection?.('all')}
              >
                Regenerate All
              </Button>
            </div>
          </div>
          <AIGeneratedTable
            frames={benchmarkFrames}
            onFrameClick={onBenchmarkFrameClick}
            onFramePreview={onBenchmarkFramePreview}
            onFrameRegenerate={onBenchmarkRegenerate}
            onFrameRemove={onBenchmarkRemove}
            onFrameUpdate={onBenchmarkUpdate}
          />
        </div>
      </div>
    </div>
  )
}
